import { useCallback, useEffect, useState } from 'react'

import type { TUsePrePromptsFromApi, TPrePromptApiResponse } from 'modules/zai/common/zai.types'
import { authService } from 'services/auth'
import { zaiChatService } from 'services/zai-chat'
import { datadog } from 'utils'

export const usePrePromptsFromApi: TUsePrePromptsFromApi = ({ language, workspaceId, errorLog }) => {
  const [data, setData] = useState<TPrePromptApiResponse>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isError, setIsError] = useState(false)

  const fetchApi = useCallback(async ({ language, workspaceId, errorLog }) => {
    const baseUrl = `${zaiChatService.baseUrl}/api/workspace/${workspaceId}/prompt`
    const params = {
      language,
    }
    const urlObject = new URL(baseUrl)

    urlObject.search = new URLSearchParams(params).toString()

    const url = urlObject.toString()

    try {
      setIsLoading(true)
      setIsError(false)

      const accessToken = await authService.getAccessToken()
      if (!accessToken) throw new Error('Missing access token')

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })

      if (!response.ok) throw new Error('Response with "ok" property value false')

      const data = ((await response.json()) ?? []) as TPrePromptApiResponse

      setData(data)
    } catch (error) {
      datadog.dispatchRequestErrorLog({
        log: errorLog,
        query: { name: url, variables: { params } },
        error,
      })

      setIsError(true)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const refetch = useCallback(() => {
    fetchApi({ language, workspaceId, errorLog })
  }, [fetchApi, language, workspaceId, errorLog])

  useEffect(() => {
    fetchApi({ language, workspaceId, errorLog })
  }, [fetchApi, language, workspaceId, errorLog])

  return {
    data,
    isLoading,
    isError,
    refetch,
  }
}
